import React from "react";
import { CustomerServiceQueries } from "../../queries/customer-service-queries";
import { Button, Col, Row, Space, Table, Typography } from "antd";
import moment from "moment";
import { DeletionApprovalStatusType } from "../../constants/deletionApprovalStatus";
import CreateDeletionRequestModal from "./modals/CreateDeletionRequest";
import RespondDeletionRequestModal from "./modals/RespondDeleteRequestModal";
import { HasRight, Rights } from "../../utilites/auth";
import { useHistory } from "react-router";

const { Title } = Typography;

export default function DeletionRequestPage() {
  const deleteRequestsQuery = CustomerServiceQueries.useGetDeleteRequests();
  const createDeleteRequestMutator = CustomerServiceQueries.useDeleteCompany();
  const updateDeleteRequestMutator = CustomerServiceQueries.useUpdateDeleteRequest();
  const [isCreateModalVisible, setIsCreateModalVisible] = React.useState<boolean>(false);
  const [isRespondModalVisible, setIsRespondModalVisible] = React.useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>([]);
  const history = useHistory();

  const columns = [
    {
      title: "Company Id",
      dataIndex: "CompanyId",
      key: "companyId",
      sorter: (a: any, b: any) => a.CompanyId - b.CompanyId,
      render: (companyId: number) => <Button
                    type='link'
                    href={`/admin/company/${companyId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      history.push(`/admin/company/${companyId}`);
                    }}
                  >{companyId}</Button>,
    },
    {
      title: "Company Name",
      dataIndex: "CompanyName",
      key: "companyName",
      sorter: (a: any, b: any) => a.CompanyName.localeCompare(b.CompanyName),
      render: (companyName: string, record: any) => <Button
                    type='link'
                    href={`/admin/company/${record.CompanyId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      history.push(`/admin/company/${record.CompanyId}`);
                    }}
                  >{companyName}</Button>,
    },
    {
      title: "Request Date",
      dataIndex: "RequestDate",
      key: "requestDate",
      render: (requestDate: string) => !!requestDate ? moment.utc(requestDate).local().format('l LT') : "",
      sorter: (a: any, b: any) => moment(a.RequestDate).diff(moment(b.RequestDate)),
      defaultSortOrder: "descend",
    },
    {
      title: "Requester Name",
      dataIndex: "RequesterName",
      key: "requesterName",
      sorter: (a: any, b: any) => a.RequesterName.localeCompare(b.RequesterName),
    },
    {
      title: "Approver Name",
      dataIndex: "ApproverName",
      key: "approverName",
      sorter: (a: any, b: any) => (a.ApproverName ?? "").localeCompare(b.ApproverName ?? ""),
    },
    {
      title: "Approval Status",
      dataIndex: "ApprovalTypeId",
      key: "approvalTypeId",
      render: (approvalTypeId: number) => DeletionApprovalStatusType[approvalTypeId]?.replace(/([a-z])([A-Z])/g, '$1 $2'),
      filters: Object.keys(DeletionApprovalStatusType).filter(key => !isNaN(Number(DeletionApprovalStatusType[key]))).map((val) => ({
        text: val?.replace(/([a-z])([A-Z])/g, '$1 $2'),
        value: DeletionApprovalStatusType[val],
      })).concat({
        text: "Not Reviewed",
        value: null,
      }),
      onFilter: (value: string, record: any) => record.ApprovalTypeId === value,
    },
    {
      title: "Action Date",
      dataIndex: "ActionDate",
      key: "actionDate",
      render: (actionDate: string) => !!actionDate ? moment.utc(actionDate).local().format('l LT') : "",
      sorter: (a: any, b: any) => moment(a.ActionDate).diff(moment(b.ActionDate)),
    },
    {
      title: "Deletion Date",
      dataIndex: "DeletionDate",
      key: "deletionDate",
      render: (deletionDate: string) => !!deletionDate ? moment.utc(deletionDate).local().format('l LT') : "",
      sorter: (a: any, b: any) => moment(a.DeletionDate).diff(moment(b.DeletionDate)),
    },
  ];

  const handleSubmit = (values: any) => {
    createDeleteRequestMutator.mutateAsync([values.companyId]).then(() => {
      setIsCreateModalVisible(false);
    });
  };

  const handleRespondSubmit = (values: any) => {
    const approvalTypeId = values.approvalTypeId;
    updateDeleteRequestMutator.mutateAsync({companyIds: selectedRowKeys, deleteStatusId: approvalTypeId}).then(() => {
      setSelectedRowKeys([]);
      setIsRespondModalVisible(false);
    });
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col flex="auto">
          <Title level={3}>Company Deletion Requests</Title>
        </Col>
        <Col>
          <Space>
            {HasRight([Rights.CSAuthorizer]) && (
              <Button
                type="primary"
                onClick={() => setIsRespondModalVisible(true)}
                disabled={selectedRowKeys.length === 0}
              >
                Respond to Selected Requests
              </Button>
            )}
            {HasRight([Rights.CSSuperUser]) && (
              <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
                Create New Request
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={deleteRequestsQuery.data}
        rowKey={record => record.Id}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedRowKeys,
          onChange: (selectedRowKeys: number[]) => {
            setSelectedRowKeys(selectedRowKeys);
          },
          getCheckboxProps: (record) => ({ disabled: (record.ApprovalTypeId ?? 0) > 0 }),
        }}
        pagination={{ defaultPageSize: 20 }}
      />
      <CreateDeletionRequestModal
        isVisible={isCreateModalVisible}
        setIsVisible={setIsCreateModalVisible}
        handleSubmit={handleSubmit}
        isLoading={createDeleteRequestMutator.isLoading}
      />
      <RespondDeletionRequestModal
        isVisible={isRespondModalVisible}
        setIsVisible={setIsRespondModalVisible}
        handleSubmit={handleRespondSubmit}
        isLoading={updateDeleteRequestMutator.isLoading}
        selectedCompanies={deleteRequestsQuery.data?.filter(x => selectedRowKeys.includes(x.Id)) ?? []}
      />
    </>
  );
}